/**
 * Live ThingsBoard adapter.
 *
 * Implements {@link EmsAdapter} so the SPA renders the same against real TB
 * data as it does against the mock. Discovers topology from the operator's
 * accessible site assets (Contains hierarchy), subscribes to per-device
 * telemetry over a single WebSocket, and proxies alarm/schedule/override
 * operations to the corresponding TB REST endpoints.
 *
 * Wire-up is mode-aware:
 *   - **embedded** (iframed): JWT comes from the host widget via postMessage
 *     (`auth.token`). REST + WS both consume that JWT.
 *   - **direct dev** (`?tb=...&token=...`): JWT comes from URL params.
 */
import {
  EmbeddedCtxSource,
  createTbHttp,
  getAlarmComments,
  getAttributes,
  getDeviceTimeseriesKeys,
  getEntityAlarms,
  listEntityGroupAssets,
  listEntityGroups,
  listRelationsTo,
  saveSharedAttribute,
  saveServerAttribute,
  ackAlarm as tbAckAlarm,
  addAlarmComment,
  clearAlarm as tbClearAlarm,
  type TbHttp,
} from "./tb";
import { BIDDING_ZONE_ASSET_TYPE } from "@/lib/marketDataAssets";
import { labelForKey } from "./tb/keyLabels";
import { TbSubscriptionManager, type TbSubState } from "./tb/ws";
import { getCurrentUser, getEntityInfo } from "./tb/entities";
import { listContainsChildren, listContainsParents } from "./tb/relations";
import type { EntityRef, TbEntity } from "./tb/types";
import { discoverSites, buildSiteContext, NoSiteAssetsError, type TbSiteContext } from "./tb/topologyFromTb";
import { discoverDayAheadPrices } from "./tb/prices";
import { TbSnapshotAssembler } from "./tb/snapshotFromTb";
import { getTimeseries } from "./tb/telemetry";
import type {
  AlarmDeviceTarget,
  AlarmTargetableDevice,
  BessEnergyHistoryQuery, BessEnergyHistoryResponse, BessEnergySample,
  EmsAdapter, EnergyGran, InspectableDevice,
  ZoneAsset, ZoneAttributeEntry, ZoneTsPoint,
} from "./adapter";
import {
  AVAILABLE_DATAPOINTS_ATTRIBUTE_KEY,
  RULES_ATTRIBUTE_KEY,
  RULES_STATUS_ATTRIBUTE_KEY,
  parseAlarmRuleEnvelope,
  parseAlarmRulesStatus,
  parseAvailableDatapoints,
  type AlarmRuleEnvelope,
  type AlarmRulesStatus,
  type AvailableDatapointsEnvelope,
} from "./alarmRules/wireFormat";
import type {
  AggregateBucket, Alarm, AlarmComment, BessUnitSnapshot, ControllerMode,
  HistoricalQuery, HistoricalResponse, OverridePayload, PricePoint,
  ScheduleDoc, ScheduleEntry, ScheduleSource, ScheduleStatus,
  SiteSnapshot, SiteTopology,
} from "./types";
import { defaultParamsFor } from "./modeCatalog";

export interface TbAdapterOptions {
  /** TB REST base — `''` when same-origin proxy, otherwise absolute. */
  restBaseUrl?: string;
  /** Separate WS base when a deploy proxies REST but not WS. */
  wsBaseUrl?: string;
  /** Hardcoded JWT for `?token=` development mode. */
  jwt?: string;
  /** When set, the embedded ctx source drives auth via postMessage. */
  embedded?: EmbeddedCtxSource;
  /** Pre-resolved site context (skip discovery). */
  site?: TbSiteContext;
  /**
   * Sibling sites the user can swap to without re-running discovery.
   * Populated by {@link TbAdapter.fromTenant} when the bound entity is a
   * `EMS Customer Grouping Asset` (Contains multiple Installations) or the
   * fallback `discoverSites()` returned more than one. The adapter itself
   * only tracks `this.site`; site swap is store-driven (it calls
   * `tryBuildTbAdapter({ entityOverride })` for the chosen sibling).
   */
  availableSites?: TbEntity[];
}

/** Compact site info surfaced to the SPA's site-switcher in live mode. */
export interface AvailableSite {
  id: string;
  name: string;
  ref: EntityRef;
}

export class TbAdapter implements EmsAdapter {
  /** UI hides/disables what the backend can't do — never placebo (ALM-03).
   *  TB has no native alarm snooze; comments round-trip via
   *  `/api/alarm/{id}/comment` (read back lazily by {@link alarmComments}). */
  readonly capabilities = { snoozeAlarms: false, alarmComments: true };
  private readonly http: TbHttp;
  private readonly ws: TbSubscriptionManager;
  private readonly assembler: TbSnapshotAssembler;
  /** Set when running iframed — used to ask the host widget for a fresh
   *  JWT (`auth.refreshNeeded`) when REST hits a terminal 401 (DOCS-03). */
  private readonly embedded?: EmbeddedCtxSource;
  private readonly authExpiredListeners = new Set<() => void>();
  /** Throttle for `auth.refreshNeeded` posts — a 401 storm (the alarm poll
   *  alone fires every 4 s) must not become a postMessage storm. */
  private lastRefreshNeededAt = 0;
  /** One-shot grace timer for embedded auth expiry — set while we wait
   *  ~15 s for the host's JWT poll to deliver a fresh token before
   *  declaring the session expired (see {@link handleAuthExpired}). */
  private authExpiryTimer: number | null = null;
  private authExpiryUnsub: (() => void) | null = null;
  readonly site: TbSiteContext;
  /**
   * Sites the user can swap to. At minimum contains `this.site` itself;
   * may contain multiple entries when the widget is bound to a grouping
   * asset (e.g. `CmN.2MN.L0.cpc inox` → Basiano + Gessate) or when
   * discovery returned multiple Installations.
   */
  readonly availableSites: ReadonlyArray<AvailableSite>;

  constructor(opts: TbAdapterOptions & { site: TbSiteContext }) {
    this.site = opts.site;
    const sibs = opts.availableSites ?? [];
    const own: AvailableSite = {
      id: opts.site.site.id,
      name: opts.site.siteName,
      ref: opts.site.site,
    };
    // Deduplicate by id; ensure the currently-active site is in the list.
    const map = new Map<string, AvailableSite>();
    map.set(own.id, own);
    for (const s of sibs) {
      if (!map.has(s.id.id)) {
        map.set(s.id.id, {
          id: s.id.id,
          name: s.label || s.name,
          ref: { type: "ASSET", id: s.id.id },
        });
      }
    }
    this.availableSites = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    const tokenStore = opts.embedded
      ? {
          getToken: () => {
            const t = opts.embedded!.getAuthToken();
            return t ? `Bearer ${t}` : null;
          },
          subscribe: (cb: (t: string | null) => void) =>
            opts.embedded!.onAuthChange((t) => cb(t ? `Bearer ${t}` : null)),
        }
      : opts.jwt
        ? { getToken: () => `Bearer ${opts.jwt}` }
        : undefined;
    this.embedded = opts.embedded;
    this.http = createTbHttp({
      baseUrl: opts.restBaseUrl ?? "",
      tokenStore,
      authHeader: "X-Authorization",
      onAuthExpired: () => this.handleAuthExpired(),
    });
    this.ws = new TbSubscriptionManager({
      baseUrl: opts.wsBaseUrl ?? opts.restBaseUrl ?? "",
      tokenProvider: opts.embedded
        ? () => opts.embedded!.getAuthToken()
        : opts.jwt
          ? () => opts.jwt ?? null
          : undefined,
    });
    this.assembler = new TbSnapshotAssembler(this.http, this.ws, this.site);
    this.assembler.start();
  }

  /**
   * Factory: resolve the operator-spa's site asset from the most specific
   * hint available and wire an adapter.
   *
   * Resolution order (most specific wins):
   *
   *   1. `opts.entity` — the entity bound to the host widget instance via
   *      `ctx.init`. This is the **canonical TB-EMS pattern**: each widget
   *      pins its own entity, so the SPA shows the site the operator
   *      chose in the dashboard. We accept:
   *        - ASSET of profile `EMS Installation` → use directly
   *        - ASSET of profile `EMS Customer Grouping Asset` → expose ALL
   *          `EMS Installation` children as sibling sites (first is the
   *          active default; the SiteSwitcher lets the operator swap).
   *        - DEVICE → walk Contains parents one level to find the site
   *   2. `discoverSites()` first match — pre-binding behaviour. All other
   *      discovered Installations are also exposed as siblings.
   *
   * Step 1 is what fixes "Tenant Admin sees Basiano only" — that was the
   * pre-binding code path picking the first-created asset across the
   * whole tenant. With proper binding, each widget instance scopes to
   * its own site, and a grouping-asset binding gives the operator a
   * picker over the sites underneath.
   */
  static async fromTenant(
    opts: Omit<TbAdapterOptions, "site"> & { entity?: EntityRef | null },
  ): Promise<TbAdapter> {
    const tokenStore = opts.embedded
      ? {
          getToken: () => {
            const t = opts.embedded!.getAuthToken();
            return t ? `Bearer ${t}` : null;
          },
        }
      : opts.jwt
        ? { getToken: () => `Bearer ${opts.jwt}` }
        : undefined;
    const probeHttp = createTbHttp({
      baseUrl: opts.restBaseUrl ?? "",
      tokenStore,
      authHeader: "X-Authorization",
    });

    let siteEntity: TbEntity | null = null;
    // Sibling Installations the operator can swap to (e.g. all sites
    // under the bound grouping asset, or the rest of discoverSites()).
    const siblings: TbEntity[] = [];

    // 1) Try the bound entity from ctx.entity
    let entityResolved = false;
    if (opts.entity?.id && opts.entity.type) {
      try {
        const info = await getEntityInfo(probeHttp, opts.entity);
        entityResolved = true;
        if (info.type === "EMS Installation" || info.type === "site") {
          siteEntity = info;
        } else if (info.type === "EMS Customer Grouping Asset") {
          // Collect EVERY Installation under this grouping. The first is
          // the default active site; the rest become siblings the
          // SiteSwitcher can swap between without leaving the iframe.
          const kids = await listContainsChildren(probeHttp, opts.entity);
          for (const k of kids) {
            const child = await getEntityInfo(probeHttp, k.child).catch(() => null);
            if (child && (child.type === "EMS Installation" || child.type === "site")) {
              if (!siteEntity) siteEntity = child;
              else siblings.push(child);
            }
          }
        } else if (opts.entity.type === "DEVICE") {
          // The widget was bound to a device — walk to the parent (the
          // device's containing site). TB doesn't expose a direct
          // "parent" endpoint, so we list the asset's Contains-children
          // approach inverted via `listRelationsTo` on the device.
          const parents = await listContainsParents(probeHttp, opts.entity);
          for (const p of parents) {
            const parent = await getEntityInfo(probeHttp, p.parent).catch(() => null);
            if (parent && (parent.type === "EMS Installation" || parent.type === "site")) {
              siteEntity = parent;
              break;
            }
          }
        }
      } catch (err) {
        console.warn("[tbAdapter] entity-hint resolution failed, falling back to discovery:", err);
      }
    }

    // The widget WAS bound to an entity and we could read it, but its hierarchy
    // held no BESS-site → that's the "demo page" case. Drop into the simulator
    // rather than widening the search to the whole tenant (which would surface
    // unrelated sites the operator didn't point this widget at).
    if (entityResolved && !siteEntity) {
      throw new NoSiteAssetsError(
        "No BESS-site found under the bound entity alias's hierarchy.",
      );
    }

    // 2) No entity bound (or its resolution errored) → discover across every
    //    site the user can see (so an unbound admin still gets the full picker,
    //    and an unbound widget with no accessible site drops into the demo).
    if (!siteEntity) {
      const sites = await discoverSites(probeHttp);
      if (sites.length === 0) {
        throw new NoSiteAssetsError(
          "No BESS-site assets visible to this TB user.",
        );
      }
      siteEntity = sites[0];
      for (let i = 1; i < sites.length; i++) siblings.push(sites[i]);
    }

    const site = await buildSiteContext(probeHttp, siteEntity);
    // A site that resolves but carries no BESS devices isn't a usable BESS-site
    // (e.g. the widget was bound to an unrelated asset) — treat it as "no
    // assets" so the simulator takes over rather than rendering an empty fleet.
    if (site.topology.bess.length === 0) {
      throw new NoSiteAssetsError("Bound entity resolved to a site with no BESS devices.");
    }
    return new TbAdapter({ ...opts, site, availableSites: siblings });
  }

  topology(): SiteTopology { return this.site.topology; }

  subscribe(handler: (snap: SiteSnapshot) => void): () => void {
    return this.assembler.onSnapshot(handler);
  }
  latest(): SiteSnapshot { return this.assembler.latest(); }

  // ------------------------------------------------------------------
  // Connection-state + auth surfaces (DATA-02 / DOCS-03)
  // ------------------------------------------------------------------

  /**
   * Re-exposes the WS manager's state stream so the store can pipe socket
   * transitions into `connection` ("reconnecting" pill, drop/recover
   * toasts). Fires on every transition, including each lap of the
   * reconnect loop. Returns an unsubscribe disposer.
   */
  onStateChange(cb: (s: TbSubState) => void): () => void {
    return this.ws.onStateChange(cb);
  }

  /** Current WS state — lets the store seed its pill without waiting for
   *  the first transition. */
  wsState(): TbSubState {
    return this.ws.getState();
  }

  /**
   * Fires when a REST call returns a terminal 401 (token refresh failed or
   * unavailable). The store uses it to surface a "session expired" state.
   * In embedded mode the adapter ALSO posts `auth.refreshNeeded` to the
   * host widget so it re-reads TB's `localStorage.jwt_token` and re-posts
   * a fresh `auth.token` (DOCS-03). Returns an unsubscribe disposer.
   */
  onAuthExpired(cb: () => void): () => void {
    this.authExpiredListeners.add(cb);
    return () => { this.authExpiredListeners.delete(cb); };
  }

  /**
   * Embedded only: fires when the host posts a DIFFERENT auth token than
   * the last one seen (the host's periodic re-post of an unchanged token
   * is filtered out). The store uses this to recover a terminal "error"
   * connection once the host's JWT poll delivers a fresh token. Returns a
   * no-op disposer in direct dev mode.
   */
  onAuthTokenChange(cb: (token: string | null) => void): () => void {
    const embedded = this.embedded;
    if (!embedded) return () => {};
    let last = embedded.getAuthToken();
    const unsub = embedded.onAuthChange((t) => {
      if (t === last) return;
      last = t;
      cb(t);
    });
    return () => { unsub(); };
  }

  private handleAuthExpired(): void {
    const now = Date.now();
    if (this.embedded && now - this.lastRefreshNeededAt > 10_000) {
      this.lastRefreshNeededAt = now;
      try {
        this.embedded.sendAction({ v: 1, type: "auth.refreshNeeded" });
      } catch { /* postMessage failure must not break the http path */ }
    }
    if (!this.embedded) {
      // Direct dev mode has no refresh path — expiry really is terminal.
      this.notifyAuthExpired();
      return;
    }
    // Embedded grace window: the host widget's 60 s JWT poll usually
    // delivers a fresh token seconds after `auth.refreshNeeded`, so the
    // FIRST 401 must not be terminal while recovery is already in flight.
    // Wait ~15 s and only notify listeners if the token is still
    // UNCHANGED; a fresh token cancels the timer (the http/ws layers read
    // the token lazily, so no further action is needed here).
    if (this.authExpiryTimer !== null) return; // grace already running
    const staleToken = this.embedded.getAuthToken();
    this.authExpiryUnsub = this.embedded.onAuthChange((t) => {
      if (t && t !== staleToken) this.cancelAuthExpiryGrace();
    });
    this.authExpiryTimer = window.setTimeout(() => {
      const current = this.embedded?.getAuthToken();
      this.cancelAuthExpiryGrace();
      if (current === staleToken) this.notifyAuthExpired();
    }, 15_000);
  }

  private cancelAuthExpiryGrace(): void {
    if (this.authExpiryTimer !== null) {
      window.clearTimeout(this.authExpiryTimer);
      this.authExpiryTimer = null;
    }
    try { this.authExpiryUnsub?.(); } catch { /* already torn down */ }
    this.authExpiryUnsub = null;
  }

  private notifyAuthExpired(): void {
    for (const cb of this.authExpiredListeners) {
      try { cb(); } catch { /* a listener error must not break the http path */ }
    }
  }

  /**
   * Backfill: query the last 60 minutes of grid + BESS + PV samples
   * from the EMS server, align them on 2-minute buckets, and produce a
   * sequence of SiteSnapshot objects that the store can drop into the
   * `recent[]` buffer immediately on adapter construction.
   *
   * Without this, the "Last 60 minutes" Overview chart shows only the
   * one or two snapshots that have arrived live since the page opened.
   * With it, the operator sees real history right away and the live
   * stream continues to extend the curve.
   *
   * Returns `[]` on REST failure rather than throwing — the live stream
   * still works; the chart simply takes the slow path to fill up.
   */
  async recentHistory(): Promise<SiteSnapshot[]> {
    const now = Date.now();
    const start = now - 60 * 60_000;
    const bucketMs = 120_000;   // 2-min uplink cadence — one bucket per uplink

    try {
      // Fetch the source series in parallel. Use AVG aggregation on the
      // 2-min grid so each device's samples land on the same bucket
      // boundaries and the per-bucket reductions are deterministic.
      //
      // `total_kW` is fetched from the same Grid device and is the
      // SINGLE source of truth for building load — we never derive it
      // from grid + pv − bess here, to keep the 60-min backfill curve
      // identical to the rest of the SPA's load surfaces (live
      // snapshot, Overview chart, Historical analyzer).
      const bessRefs = Object.entries(this.site.refs.bess);  // [bessId, ref][]
      const [gridSeries, totalSeries, pvSeries, bessSeries] = await Promise.all([
        this.site.refs.grid
          ? fetchAgg(this.http, this.site.refs.grid, "grid_kW", start, now, bucketMs)
          : Promise.resolve([]),
        this.site.refs.grid
          ? fetchAgg(this.http, this.site.refs.grid, "total_kW", start, now, bucketMs)
          : Promise.resolve([]),
        this.site.refs.pv
          ? fetchAgg(this.http, this.site.refs.pv, "pv_power_kW", start, now, bucketMs)
          : Promise.resolve([]),
        Promise.all(bessRefs.map(async ([bessId, ref]) => {
          const [p, soc] = await Promise.all([
            fetchAgg(this.http, ref, "battery_kW", start, now, bucketMs),
            fetchAgg(this.http, ref, "battery_soc_%", start, now, bucketMs),
          ]);
          return { bessId, p, soc };
        })),
      ]);

      // Collect every distinct bucket ts the server returned anywhere.
      const allTs = new Set<number>();
      for (const p of gridSeries) allTs.add(p.ts);
      for (const p of totalSeries) allTs.add(p.ts);
      for (const p of pvSeries) allTs.add(p.ts);
      for (const b of bessSeries) {
        for (const p of b.p) allTs.add(p.ts);
        for (const p of b.soc) allTs.add(p.ts);
      }
      const buckets = [...allTs].sort((a, b) => a - b);
      if (!buckets.length) return [];

      // Build per-series ts → value maps for O(1) lookup.
      const gridMap = new Map(gridSeries.map((p) => [p.ts, p.value]));
      const totalMap = new Map(totalSeries.map((p) => [p.ts, p.value]));
      const pvMap = new Map(pvSeries.map((p) => [p.ts, p.value]));
      const bessMaps = bessSeries.map((b) => ({
        bessId: b.bessId,
        p: new Map(b.p.map((x) => [x.ts, x.value])),
        soc: new Map(b.soc.map((x) => [x.ts, x.value])),
      }));

      // Forward-fill each series so a bucket with no fresh sample still
      // carries the last-known value (same approach as `history()`).
      let lastGrid = NaN;
      let lastTotal = NaN;
      let lastPv = NaN;
      const lastBessP: Record<string, number> = {};
      const lastBessSoc: Record<string, number> = {};

      const template = this.assembler.latest();  // for fields we don't backfill
      const snapshots: SiteSnapshot[] = [];

      for (const ts of buckets) {
        const gv = gridMap.get(ts);
        if (gv !== undefined && Number.isFinite(gv)) lastGrid = gv;
        const tv = totalMap.get(ts);
        if (tv !== undefined && Number.isFinite(tv)) lastTotal = tv;
        const pvv = pvMap.get(ts);
        if (pvv !== undefined && Number.isFinite(pvv)) lastPv = pvv;
        for (const bm of bessMaps) {
          const p = bm.p.get(ts);
          if (p !== undefined && Number.isFinite(p)) lastBessP[bm.bessId] = p;
          const soc = bm.soc.get(ts);
          if (soc !== undefined && Number.isFinite(soc)) lastBessSoc[bm.bessId] = soc;
        }

        // Wait until at least the grid AND every BESS device have been
        // seen at least once before emitting — otherwise the leading
        // edge undercounts the fleet and produces the "ramps up to
        // steady state" artifact.
        if (!Number.isFinite(lastGrid)) continue;
        if (bessMaps.some((bm) => !Number.isFinite(lastBessP[bm.bessId] ?? NaN))) continue;

        // Grid + PV already canonical (positive = importing / generating)
        // per SIGN_CONVENTION — just scale kW → W.
        const grid_W = lastGrid * 1000;
        const pv_W = Number.isFinite(lastPv) ? lastPv * 1000 : 0;

        // Per-unit BESS array — use the controller's commanded order so
        // the array indices match what `bess_per_unit` shows live.
        const perUnit: BessUnitSnapshot[] = this.site.topology.bess.map((b) => {
          const liveU = template.bess_per_unit.find((x) => x.id === b.id);
          const kw = lastBessP[b.id] ?? NaN;
          const soc = lastBessSoc[b.id] ?? NaN;
          return {
            id: b.id,
            name: liveU?.name ?? b.name,
            online: Number.isFinite(kw),
            // EMS server: positive battery_kW = discharging.
            // SPA canonical: positive = charging.
            active_W: Number.isFinite(kw) ? -kw * 1000 : 0,
            reactive_VAr: 0,
            soc_pct: Number.isFinite(soc) ? soc : 0,
            total_voltage_V: liveU?.total_voltage_V ?? 0,
            ambient_temperature_C: liveU?.ambient_temperature_C ?? NaN,
            fault_count: 0,
            setpoint_W: 0,
            bess_onoff: false,
            heartbeat_age_s: 0,
          };
        });

        const bess_total_W = perUnit.reduce((s, u) => s + u.active_W, 0);
        const socVals = perUnit.map((u) => u.soc_pct).filter((s) => Number.isFinite(s));
        const onlineCount = perUnit.filter((u) => u.online).length;

        // Net building load: ONLY from the Grid device's `total_kW` —
        // never derived. If the grid device doesn't publish `total_kW`
        // (or hasn't yet for this bucket), the value reads as 0 rather
        // than mixing in a derived approximation. The sign is
        // preserved — positive = net consumption, negative = on-site
        // generation exceeding load.
        const load_W = Number.isFinite(lastTotal) ? lastTotal * 1000 : 0;

        snapshots.push({
          ts,
          connection: "live",
          grid: {
            active_W: grid_W,
            reactive_VAr: 0,
            voltage_V: template.grid.voltage_V,
            current_A: template.grid.current_A,
            frequency_Hz: template.grid.frequency_Hz,
            pf: template.grid.pf,
          },
          bess_aggregate: {
            active_W: bess_total_W,
            reactive_VAr: 0,
            soc_avg_pct: socVals.length ? socVals.reduce((a, b) => a + b, 0) / socVals.length : 0,
            soc_min_pct: socVals.length ? Math.min(...socVals) : 0,
            soc_max_pct: socVals.length ? Math.max(...socVals) : 0,
            online_count: onlineCount,
            fault_count: 0,
            total_capacity_kWh: template.bess_aggregate.total_capacity_kWh,
            total_voltage_V: template.bess_aggregate.total_voltage_V,
            avg_temperature_C: template.bess_aggregate.avg_temperature_C,
          },
          bess_per_unit: perUnit,
          pv: this.site.refs.pv ? { active_W: pv_W, today_kWh: 0, rated_kWp: template.pv?.rated_kWp ?? 0 } : undefined,
          building_load_W: load_W,
          net_at_meter_W: grid_W,
          controller: template.controller,
          health: template.health,
          energy_today: template.energy_today,
        });
      }
      return snapshots;
    } catch (err) {
      console.warn("[operator-spa] recentHistory backfill failed; live stream will fill the buffer over time:", err);
      return [];
    }
  }

  /**
   * Historical timeseries — maps the SPA's abstract keys onto the real
   * TB entity + telemetry-key combinations.
   *
   * Abstract keys we accept (the only ones the Historical page asks for):
   *
   *   grid_W                     →  grid device, `grid_kW`           (× 1000; canonical sign)
   *   pv_power_W                 →  pv device,   `pv_power_kW`       (× 1000)
   *   battery/aggregation/power  →  sum across BESS devices, `battery_kW` (× -1000; TB flips sign)
   *   battery/aggregation/SOC    →  avg across BESS devices, `battery_soc_%`
   *   price_eur_mwh              →  site attribute timeline (typically empty until tariff source wired)
   *
   * If a topology lacks the underlying device (no PV, no grid, etc.) the
   * series is returned empty — the chart shows a gap rather than crashing.
   * Unknown keys also return an empty series (forward-compatible).
   */
  async history(query: HistoricalQuery): Promise<HistoricalResponse> {
    const interval = granToMs(query.granularity);
    // TB's `agg=AVG` returns HTTP 400 on some deployments when the
    // requested interval is smaller than what the server is willing to
    // group on the fly — typically anything finer than ~15 min on a
    // PE-tier ThingsBoard or any deployment using `usage.tenant.maxIntervalsPerRequest`
    // below the bucket count. The edge uplinks at roughly 2-min cadence,
    // so for ≤ 5-min granularities the natural fix is to ask for RAW
    // and bucket on the client into the requested interval — this is
    // cheap (a 72 h window @ 2 min ≈ 2160 raw points per key) and
    // works on every TB version regardless of server-side limits.
    const FINE_INTERVAL_MS = 15 * 60_000;
    const useClientBucket = interval !== undefined && interval < FINE_INTERVAL_MS;
    const remoteAgg = useClientBucket || !interval ? "NONE" : "AVG";
    const remoteInterval = useClientBucket ? undefined : interval;
    // Bucket size for cross-device alignment + client-side AVG.
    const bucketMs = interval ?? 60_000;
    const series: Record<string, Array<{ ts: number; value: number }>> = {};

    /** Fetch one TB key from one entity. Returns [] on miss. When the
     *  requested granularity is finer than what TB tolerates for AVG,
     *  fall back to raw + bucket locally so the chart still has data. */
    const fetch = async (entity: EntityRef, key: string): Promise<Array<{ ts: number; value: number }>> => {
      try {
        const raw = await getTimeseries(this.http, entity, {
          keys: [key],
          startTs: query.start_ts,
          endTs: query.end_ts,
          limit: 50_000,
          agg: remoteAgg,
          interval: remoteInterval,
        });
        const arr = raw[key] ?? [];
        const points = arr.map((p) => ({
          ts: p.ts,
          value: typeof p.value === "number" ? p.value : Number(p.value),
        })).filter((p) => Number.isFinite(p.value));
        // Client-side AVG bucketing for fine intervals. The single-series
        // bucketer uses alignedAggregate with a one-element list.
        return useClientBucket ? alignedAverage([points], bucketMs) : points;
      } catch {
        return [];
      }
    };

    // Per-BESS request path — when the SPA passes `bess_id`, the abstract
    // catalog keys (ac_active_power, soc, total_voltage, …) map to the
    // single BESS device's TB telemetry keys with the right scale + sign.
    const perBessRef = query.bess_id ? this.site.refs.bess[query.bess_id] : null;

    for (const k of query.keys) {
      // === per-BESS (telemetry browser on the BESS detail page) =======
      if (perBessRef) {
        const m = BESS_KEY_MAP[k];
        if (m) {
          const pts = await fetch(m.controller ? this.site.refs.controller ?? perBessRef : perBessRef, m.tbKey);
          series[k] = m.scale === 1 ? pts : pts.map((p) => ({ ts: p.ts, value: p.value * m.scale }));
          continue;
        }
        // Unknown per-BESS key — try the literal key on the BESS device
        // (forward-compatible with future cloud-connector additions).
        series[k] = await fetch(perBessRef, k);
        continue;
      }

      // === site-level keys (Historical / Overview) ==================
      if (k === "grid_W") {
        if (!this.site.refs.grid) { series[k] = []; continue; }
        const pts = await fetch(this.site.refs.grid, "grid_kW");
        // Canonical SIGN_CONVENTION on both sides: positive = importing.
        series[k] = pts.map((p) => ({ ts: p.ts, value: p.value * 1000 }));
      } else if (k === "pv_power_W") {
        if (!this.site.refs.pv) { series[k] = []; continue; }
        const pts = await fetch(this.site.refs.pv, "pv_power_kW");
        series[k] = pts.map((p) => ({ ts: p.ts, value: p.value * 1000 }));
      } else if (k === "battery/aggregation/power") {
        const bessRefs = Object.values(this.site.refs.bess);
        if (!bessRefs.length) { series[k] = []; continue; }
        const all = await Promise.all(bessRefs.map((r) => fetch(r, "battery_kW")));
        series[k] = alignedSum(all, bucketMs).map((p) => ({ ts: p.ts, value: -p.value * 1000 }));
      } else if (k === "battery/aggregation/SOC") {
        const bessRefs = Object.values(this.site.refs.bess);
        if (!bessRefs.length) { series[k] = []; continue; }
        const all = await Promise.all(bessRefs.map((r) => fetch(r, "battery_soc_%")));
        series[k] = alignedAverage(all, bucketMs);
      } else if (k === "building_load_W") {
        // SINGLE source of truth: the Grid device's `total_kW` key —
        // the EMS server's cumulative active-power reading across all
        // phases at the metering point. Published with canonical
        // SIGN_CONVENTION (positive = consumption), so a straight
        // × 1000 scale is enough.
        //
        // We deliberately do NOT fall back to a derived calculation
        // (grid + pv − bess) because:
        //   1. derived load disagrees with the smartmeter at sites
        //      where there's any flow that doesn't pass the BESS
        //      branch (parasitic consumers, PV losses, sub-meter drift);
        //   2. a chart that silently switches between two sources is
        //      worse than one that breaks honestly when the authoritative
        //      key is missing — the operator can then fix the data path
        //      instead of being misled by a near-but-not-quite curve.
        //
        // If `total_kW` isn't published, the series is empty and the
        // chart renders a clean break (per the unified gap policy in
        // chartGaps).
        if (this.site.refs.grid) {
          const totalP = await fetch(this.site.refs.grid, "total_kW");
          // No sign clamp — the smartmeter's cumulative reading can
          // legitimately go negative when on-site generation exceeds
          // load (PV surplus). The series is a NET building load:
          // positive = net consumption, negative = net production.
          series[k] = totalP.map((p) => ({
            ts: p.ts,
            value: p.value * 1000,
          }));
        } else {
          series[k] = [];
        }
      } else if (k === "price_eur_mwh") {
        // Day-ahead price from the bidding-zone asset. Same source as
        // `prices()`; we just present it as an aligned series for the
        // Historical analyzer.
        const pts = await discoverDayAheadPrices(this.http, query.start_ts, query.end_ts).catch(() => []);
        series[k] = pts.map((p) => ({ ts: p.ts, value: p.price }));
      } else if (k.startsWith("device:")) {
        // Per-device direct lookup: `device:<entityId>:<rawKey>`.
        // Used by the operator-facing "Browse all telemetry" panel
        // which lets the operator inspect each device's own keys
        // without the curated abstraction layer.
        const [, entityId, ...keyParts] = k.split(":");
        const rawKey = keyParts.join(":");
        const entity = this.resolveDeviceEntity(entityId);
        if (!entity || !rawKey) { series[k] = []; continue; }
        const pts = await fetch(entity, rawKey);
        series[k] = pts;
      } else {
        // Forward-compatibility: pass through unknown keys against the site
        // asset (in case the EMS server has additional series there).
        series[k] = await fetch(this.site.site, k);
      }
    }
    return { series };
  }

  /** Resolve a device entity id to an EntityRef by searching the site
   *  refs (grid, pv, controller, aux, bess). Returns null on unknown. */
  private resolveDeviceEntity(id: string): EntityRef | null {
    if (this.site.refs.grid?.id === id) return this.site.refs.grid;
    if (this.site.refs.pv?.id === id) return this.site.refs.pv;
    if (this.site.refs.controller?.id === id) return this.site.refs.controller;
    for (const a of this.site.refs.aux ?? []) if (a.id === id) return a;
    for (const b of Object.values(this.site.refs.bess)) if (b.id === id) return b;
    return null;
  }

  /**
   * Device-grouped catalog discovered LIVE from the EMS server. For
   * each bound device we query the TB `/keys/timeseries` endpoint and
   * receive every key that has ever been published to that entity —
   * raw cloud-connector device keys (e.g. `battery_kW`, `grid_kW`),
   * cumulative counters (`EP`, `EN`), AND every AE2-derived bucket
   * (`EP_d`, `EN_h`, `P_EP_5`, `EP_d_consumption`, …).
   *
   * Friendly labels come from {@link labelForKey} which uses an exact
   * dictionary, then the AE2-pattern decoder, then a suffix fallback.
   *
   * Failures per-device degrade silently (the device row gets an empty
   * keys array; the Historical panel hides it via its existing filter
   * for empty rows). The user can still browse devices that succeeded.
   */
  async inspectableSeries(): Promise<InspectableDevice[]> {
    type DeviceSpec = {
      entityId: string;
      kind: InspectableDevice["kind"];
      label: string;
    };
    const specs: DeviceSpec[] = [];

    if (this.site.refs.grid) {
      specs.push({ entityId: this.site.refs.grid.id, kind: "grid", label: "Grid coupling" });
    }
    Object.entries(this.site.refs.bess).forEach(([bessId, entity], idx) => {
      const cfg = this.site.topology.bess.find((b) => b.id === bessId);
      specs.push({
        entityId: entity.id,
        kind: "bess",
        label: cfg?.name || `Battery #${idx + 1}`,
      });
    });
    if (this.site.refs.controller) {
      specs.push({ entityId: this.site.refs.controller.id, kind: "controller", label: "BESS controller" });
    }
    (this.site.refs.aux ?? []).forEach((entity, idx) => {
      specs.push({
        entityId: entity.id,
        kind: "aux",
        label: idx === 0 ? "Transformer · LV side" : `Auxiliary #${idx + 1}`,
      });
    });
    if (this.site.refs.pv) {
      specs.push({ entityId: this.site.refs.pv.id, kind: "pv", label: "Solar PV" });
    }

    // Discover keys per device in parallel. Each device query is wrapped
    // in catch so one slow / failing device doesn't block the rest.
    const discovered = await Promise.all(
      specs.map(async (s) => {
        const keys = await getDeviceTimeseriesKeys(this.http, { type: "DEVICE", id: s.entityId });
        const sorted = sortKeysForOperator(keys);
        return {
          id: s.entityId,
          kind: s.kind,
          label: s.label,
          keys: sorted.map((rawKey) => {
            const meta = labelForKey(rawKey);
            return {
              key: `device:${s.entityId}:${rawKey}`,
              label: meta.label,
              unit: meta.unit,
              scale: meta.scale,
              signFlip: meta.signFlip,
              chart: meta.chart,
            };
          }),
        };
      }),
    );

    return discovered;
  }

  /**
   * Bucketed energy history per BESS unit. Queries the AE2-derived
   * `EP_<gran>` (charged kWh) and `EN_<gran>` (discharged kWh)
   * timeseries on each per-BESS device, aligns by ts, and returns
   * per-BESS sample arrays so the UI can render a single unit or sum
   * across the fleet.
   */
  async bessEnergyHistory(opts: BessEnergyHistoryQuery): Promise<BessEnergyHistoryResponse> {
    const epKey = `EP_${tbBucketSuffix(opts.granularity)}`;
    const enKey = `EN_${tbBucketSuffix(opts.granularity)}`;
    const allBess = Object.entries(this.site.refs.bess);
    const bessFilter = new Set(opts.bessIds ?? allBess.map(([id]) => id));
    const perBess: Record<string, BessEnergySample[]> = {};

    await Promise.all(allBess.map(async ([bessId, ref]) => {
      if (!bessFilter.has(bessId)) return;
      try {
        const raw = await getTimeseries(this.http, ref, {
          keys: [epKey, enKey],
          startTs: opts.start_ts,
          endTs: opts.end_ts,
          limit: 50_000,
          agg: "NONE",   // AE2 already pre-bucketed; let the server stream raw points
        });
        const ep = (raw[epKey] ?? []);
        const en = (raw[enKey] ?? []);
        perBess[bessId] = alignChargeDischarge(ep, en);
      } catch {
        perBess[bessId] = [];
      }
    }));
    return { granularity: opts.granularity, perBess };
  }

  async prices(start_ts: number, hours: number): Promise<PricePoint[]> {
    // Day-ahead prices live on `electricity_bidding_zone` assets that
    // the EMS server's market-prices analytics provision. We discover
    // the first accessible zone, parse `next_day_prices` +
    // `recent_days_ring`, and convert each (date, hour, timezone)
    // tuple to an absolute epoch-ms PricePoint.
    const end_ts = start_ts + hours * 3_600_000;
    return discoverDayAheadPrices(this.http, start_ts, end_ts);
  }

  async buckets(_s: number, _e: number, _g: "h" | "d" | "w" | "m"): Promise<AggregateBucket[]> {
    // Real implementation: read EP_h/EP_d/EP_w/EP_m + EN_* + price overlay
    // for the requested range. Stubbed for now — pages render an empty
    // chart rather than crashing.
    return [];
  }

  /** Every entity the edge can raise a native alarm on: the site asset PLUS
   *  all device refs (grid / controller / pv / bess / aux). The edge alarm
   *  engine raises NATIVE alarms on the DEVICES and propagates them to TENANT
   *  (not via asset relations), so querying only the site asset misses them.
   *  Fan out across the devices too. Deduped by `type:id`. */
  private alarmEntities(): EntityRef[] {
    const r = this.site.refs;
    const list: EntityRef[] = [this.site.site];
    if (r.grid) list.push(r.grid);
    if (r.controller) list.push(r.controller);
    if (r.pv) list.push(r.pv);
    for (const ref of Object.values(r.bess)) list.push(ref);
    for (const ref of r.aux) list.push(ref);
    const seen = new Set<string>();
    return list.filter((e) => {
      const k = `${e.type}:${e.id}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }

  async alarms(): Promise<Alarm[]> {
    // Fan out across the site asset + every device ref, merge + dedupe by alarm
    // id. Each query is resilient — a failed one contributes no rows rather than
    // failing the whole alarm view.
    const perEntity = await Promise.all(
      this.alarmEntities().map((e) =>
        getEntityAlarms(this.http, e, { searchStatus: "ANY", pageSize: 100 })
          .then((p) => p.data)
          .catch(() => []),
      ),
    );
    const seenAlarm = new Set<string>();
    const rows = perEntity.flat().filter((a) => {
      if (seenAlarm.has(a.id.id)) return false;
      seenAlarm.add(a.id.id);
      return true;
    });
    return rows.map((a) => {
      // ALM-04: the edge alarm engine writes its rule type into the TB alarm
      // `details` (cloud-connector-v3 alarm_integration). Map it when present
      // and valid; otherwise leave the field EMPTY so the UI omits the chip
      // instead of asserting "threshold" for e.g. comm-loss alarms — a wrong
      // label is worse than no label. The cast is required because the Alarm
      // union has no neutral member; the alarms page hides empty chips.
      const detailRuleType = String((a.details ?? {}).rule_type ?? "");
      const rule_type = (
        VALID_RULE_TYPES.has(detailRuleType) ? detailRuleType : ""
      ) as Alarm["rule_type"];
      return {
        id: a.id.id,
        device_id: a.originator.id,
        device_name: a.originatorName ?? a.originator.id,
        rule_type,
        // The edge engine writes the firing topic as `details.datapoint`;
        // older payloads used `source_topic`. Accept either so the column fills.
        source_topic: String((a.details ?? {}).source_topic ?? (a.details ?? {}).datapoint ?? ""),
        severity: a.severity === "CRITICAL" ? "critical" as const
          : a.severity === "MAJOR" ? "major" as const
          : a.severity === "MINOR" ? "minor" as const
          : "warning" as const,
        status: a.status.startsWith("ACTIVE_") ? (a.status === "ACTIVE_ACK" ? "acknowledged" as const : "active" as const) : "cleared" as const,
        message: a.name,
        detail: String((a.details ?? {}).message ?? a.name),
        raised_at: a.startTs,
        ack_at: a.ackTs,
        // TB's alarm DTO records WHO only for assignment (assigneeId), not
        // for acknowledgement — there is no ack-user field to map, so this
        // stays honestly undefined. (TB ≥ 3.5 records the ack user in an
        // auto-generated SYSTEM alarm comment, not on the alarm itself.)
        ack_by: undefined,
        cleared_at: a.clearTs,
        // Comments are read back lazily via alarmComments() on row expand —
        // never per-alarm here (100 alarms × N requests on a 4 s poll).
        comments: [],
      };
    });
  }

  /**
   * Lazy comment read-back (ALM-03) — the alarms page calls this when a
   * row expands, NOT per-alarm in the list call. Maps TB's
   * `AlarmCommentInfo` DTO to the SPA shape. TB "SYSTEM" comments
   * (auto-generated ack/clear audit lines) are filtered out: the row
   * already renders ack/clear state, and the notes panel is for operator
   * prose.
   */
  async alarmComments(id: string): Promise<AlarmComment[]> {
    const page = await getAlarmComments(this.http, id, { pageSize: 100 });
    return page.data
      .filter((c) => c.type !== "SYSTEM")
      .map((c) => ({
        id: c.id.id,
        at: c.createdTime,
        by: [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || c.email || "operator",
        text: typeof c.comment?.text === "string" ? c.comment.text : "",
      }))
      .filter((c) => c.text.length > 0);
  }
  bessIdForAlarm(alarm: Alarm): string | null {
    // Live alarms carry the TB originator UUID; topology units use the
    // synthetic "bess-N" ids. Exact reverse lookup over the device refs,
    // with a device-name fallback for alarms raised on renamed devices.
    for (const [bessId, ref] of Object.entries(this.site.refs.bess)) {
      if (ref.id === alarm.device_id) return bessId;
    }
    return this.site.topology.bess.find((b) => b.name === alarm.device_name)?.id ?? null;
  }

  deviceTargetForAlarm(alarm: Alarm): AlarmDeviceTarget | null {
    // BESS first — reuse the originator-UUID reverse lookup above.
    const bessId = this.bessIdForAlarm(alarm);
    if (bessId) return { kind: "bess", route: `/bess/${bessId}`, labelKey: "alarms.openDevice" };
    const refs = this.site.refs;
    // Grid/meter originator (grid or pv ref) → the grid_W trace, NOT /site
    // (read-only config with no live value). Same kind logic as
    // inspectableSeries / resolveDeviceEntity.
    if (refs.grid?.id === alarm.device_id || refs.pv?.id === alarm.device_id) {
      return { kind: "grid", route: "/historical", labelKey: "alarms.viewMeter" };
    }
    // Controller / EMS-health originator (controller or aux ref) → /controller.
    if (
      refs.controller?.id === alarm.device_id ||
      (refs.aux ?? []).some((a) => a.id === alarm.device_id)
    ) {
      return { kind: "controller", route: "/controller", labelKey: "alarms.openController" };
    }
    // Unknown originator → no extra link (preserve the null-fallback discipline).
    return null;
  }

  historicalKeysFor(unitId: string, catalogKeys: string[]): string[] {
    // Catalog key → raw TB key via BESS_KEY_MAP; commanded-setpoint keys
    // live on the controller device, everything else on the unit itself.
    const bessRef = this.site.refs.bess[unitId];
    const ctrlRef = this.site.refs.controller;
    const out: string[] = [];
    for (const k of catalogKeys) {
      const m = BESS_KEY_MAP[k];
      if (!m) continue;
      const ref = m.controller ? ctrlRef : bessRef;
      if (!ref) continue;
      out.push(`device:${ref.id}:${m.tbKey}`);
    }
    return out;
  }

  async ackAlarm(id: string, _by: string): Promise<void> { return tbAckAlarm(this.http, id); }
  async clearAlarm(id: string, _by: string): Promise<void> { return tbClearAlarm(this.http, id); }
  async commentAlarm(id: string, _by: string, text: string): Promise<void> { await addAlarmComment(this.http, id, text); }
  async snoozeAlarm(_id: string, _by: string, _until: number): Promise<void> {
    // TB has no native snooze; would write a SERVER_SCOPE attribute on the alarm originator.
    // Tracked in TB_INTEGRATION.md.
  }

  async scheduleEntries(): Promise<ScheduleEntry[]> {
    const doc = await this.getScheduleDoc();
    return doc?.entries ?? [];
  }

  async getScheduleDoc(): Promise<ScheduleDoc | null> {
    if (!this.site.refs.controller) return null;
    const env = await this.readScheduleEnvelope();
    if (!env) return null;
    return envelopeToDoc(env, this.site.topology.timezone || "Europe/Rome");
  }

  /**
   * Read the FULL bess_schedule envelope (version + timezone + default +
   * entries + operator meta) for a non-destructive round-trip. Used by
   * saveScheduleDoc so we never overwrite operator-configured fields.
   */
  private async readScheduleEnvelope(): Promise<ScheduleEnvelope | null> {
    if (!this.site.refs.controller) return null;
    const a = await getAttributes(this.http, this.site.refs.controller, "SHARED_SCOPE", ["bess_schedule"]).catch(() => []);
    const raw = a.find((x) => x.key === "bess_schedule")?.value;
    if (!raw || typeof raw !== "object") return null;
    return raw as ScheduleEnvelope;
  }

  async schedule(): Promise<ScheduleSource> {
    const doc = await this.getScheduleDoc();
    const entries = doc?.entries ?? [];
    const tz = doc?.timezone || this.site.topology.timezone || "Europe/Rome";
    const active = resolveActiveEntry(entries, tz, Date.now());
    const next = nextChangeAfter(entries, doc?.default.mode ?? "gridZeroControl", tz, Date.now());
    const src: ScheduleSource = {
      source: "cloud",
      doc: doc ?? undefined,
      active_now: active
        ? { mode: active.mode, entry: active }
        : { mode: doc?.default.mode ?? "gridZeroControl" },
      next_change: next,
    };
    // An in-effect manual override beats the schedule. Surface it so the
    // page renders the amber "override active" strip + the Clear button
    // (the edge's OverrideIntegration has already applied it on-disk).
    const override = await this.readActiveOverride();
    if (override) {
      src.source = "manual_override";
      src.override = override;
      src.active_now = { mode: override.mode };
    }
    return src;
  }

  /**
   * Read the `bess_manual_override` SHARED_SCOPE attribute and return it
   * only if it's still in effect (expiry in the future). This is the
   * operator's own command echoed back — the edge consumes the same
   * attribute and lands `manual_override.json` from it.
   */
  private async readActiveOverride(): Promise<ScheduleSource["override"] | undefined> {
    if (!this.site.refs.controller) return undefined;
    const a = await getAttributes(
      this.http, this.site.refs.controller, "SHARED_SCOPE", ["bess_manual_override"],
    ).catch(() => []);
    const raw = a.find((x) => x.key === "bess_manual_override")?.value;
    if (!raw || typeof raw !== "object") return undefined;
    const o = raw as { mode?: ControllerMode; expires_at_ms?: number; params?: Record<string, number>; reason?: string };
    if (!o.mode || typeof o.expires_at_ms !== "number") return undefined;
    if (o.expires_at_ms <= Date.now()) return undefined;   // already expired — not active
    return { mode: o.mode, expires_at: o.expires_at_ms, params: o.params, reason: o.reason };
  }

  async saveScheduleEntries(entries: ScheduleEntry[]): Promise<void> {
    // Legacy convenience — preserve the existing default + timezone.
    const existing = await this.getScheduleDoc();
    const doc: ScheduleDoc = {
      version: 1,
      timezone: existing?.timezone || this.site.topology.timezone || "Europe/Rome",
      default: existing?.default ?? { mode: "gridZeroControl", params: {} },
      entries,
      meta: existing?.meta,
    };
    return this.saveScheduleDoc(doc);
  }

  async saveScheduleDoc(doc: ScheduleDoc): Promise<void> {
    if (!this.site.refs.controller) {
      throw new Error("No controller device bound to this site — cannot save schedule.");
    }
    // Wire payload mirrors the cloud-connector-v3 BESS_SCHEDULE_ATTRIBUTE spec:
    //   { version: 1, updatedAt, updatedBy?, name?, description?, timezone,
    //     default: { mode, params }, entries: [...] }
    // The four optional top-level meta fields (name, description, updatedBy,
    // updatedAt) are required for the edge's status echo to identify which
    // save it's ACKing — `updatedAt` is the dedup key.
    const updatedAt = Date.now();
    const wire = {
      version: 1 as const,
      updatedAt,
      ...(doc.meta?.updatedBy ? { updatedBy: doc.meta.updatedBy } : {}),
      ...(doc.meta?.name ? { name: doc.meta.name } : {}),
      ...(doc.meta?.description ? { description: doc.meta.description } : {}),
      timezone: doc.timezone || this.site.topology.timezone || "Europe/Rome",
      default: {
        mode: doc.default.mode,
        params: doc.default.params ?? {},
      },
      entries: doc.entries.map(internalEntryToWire),
    };
    await saveSharedAttribute(this.http, this.site.refs.controller, {
      bess_schedule: wire,
    });
  }

  async getScheduleStatus(): Promise<ScheduleStatus | null> {
    if (!this.site.refs.controller) return null;
    const a = await getAttributes(
      this.http, this.site.refs.controller, "SHARED_SCOPE", ["bess_schedule_status"],
    ).catch(() => []);
    return parseScheduleStatus(a.find((x) => x.key === "bess_schedule_status")?.value);
  }

  async getOverrideStatus(): Promise<ScheduleStatus | null> {
    if (!this.site.refs.controller) return null;
    const a = await getAttributes(
      this.http, this.site.refs.controller, "SERVER_SCOPE", ["bess_manual_override_status"],
    ).catch(() => []);
    return parseScheduleStatus(a.find((x) => x.key === "bess_manual_override_status")?.value);
  }

  async setOverride(p: OverridePayload | null): Promise<void> {
    if (!this.site.refs.controller) return;
    if (!p) {
      // Clearing: a null `bess_manual_override` tells the edge's
      // OverrideIntegration to remove manual_override.json so the
      // controller falls back to the schedule.
      await saveSharedAttribute(this.http, this.site.refs.controller, { bess_manual_override: null });
      return;
    }
    // Wire shape mirrors EMS-Admin's `ManualOverride` ({mode, params,
    // expires_at_ms, reason}) plus a transport-only `updatedAt` the edge
    // uses for dedup. `params` defaults to the mode's defaults so a
    // caller that doesn't collect them (e.g. the command-palette
    // quick-switch) still produces a payload the edge validator accepts.
    await saveSharedAttribute(this.http, this.site.refs.controller, {
      bess_manual_override: {
        mode: p.mode,
        params: p.params ?? defaultParamsFor(p.mode),
        expires_at_ms: p.expires_at,
        reason: p.reason ?? `operator-spa: ${p.mode}`,
        updatedAt: Date.now(),
      },
    });
  }
  async setActiveMode(mode: ControllerMode): Promise<void> {
    return this.setOverride({ mode, expires_at: Date.now() + 30 * 60_000 });
  }

  // ---------------------------------------------------------------------
  // Alarm rule authoring (cloud → edge via TB shared/server attributes)
  // ---------------------------------------------------------------------

  async listAlarmRules(deviceId: string): Promise<AlarmRuleEnvelope> {
    const a = await getAttributes(
      this.http,
      { type: "DEVICE", id: deviceId },
      "SHARED_SCOPE",
      [RULES_ATTRIBUTE_KEY],
    ).catch(() => []);
    const raw = a.find((x) => x.key === RULES_ATTRIBUTE_KEY)?.value;
    return parseAlarmRuleEnvelope(raw);
  }

  async saveAlarmRules(deviceId: string, envelope: AlarmRuleEnvelope): Promise<void> {
    // Stamp `updatedAt` on every save so the edge's dedup uses our
    // version, not whatever the caller may have left stale.
    const payload: AlarmRuleEnvelope = {
      ...envelope,
      updatedAt: Date.now(),
    };
    await saveSharedAttribute(
      this.http,
      { type: "DEVICE", id: deviceId },
      { [RULES_ATTRIBUTE_KEY]: payload },
    );
  }

  async getAlarmRulesStatus(deviceId: string): Promise<AlarmRulesStatus | null> {
    const a = await getAttributes(
      this.http,
      { type: "DEVICE", id: deviceId },
      "SERVER_SCOPE",
      [RULES_STATUS_ATTRIBUTE_KEY],
    ).catch(() => []);
    const raw = a.find((x) => x.key === RULES_STATUS_ATTRIBUTE_KEY)?.value;
    return parseAlarmRulesStatus(raw);
  }

  async getAvailableDatapoints(deviceId: string): Promise<AvailableDatapointsEnvelope> {
    const a = await getAttributes(
      this.http,
      { type: "DEVICE", id: deviceId },
      "SERVER_SCOPE",
      [AVAILABLE_DATAPOINTS_ATTRIBUTE_KEY],
    ).catch(() => []);
    const raw = a.find((x) => x.key === AVAILABLE_DATAPOINTS_ATTRIBUTE_KEY)?.value;
    return parseAvailableDatapoints(raw);
  }

  async listTargetableDevices(): Promise<AlarmTargetableDevice[]> {
    const out: AlarmTargetableDevice[] = [];
    const seen = new Set<string>();
    const push = (
      ref: EntityRef | null | undefined,
      name: string,
      kind: AlarmTargetableDevice["kind"],
    ): void => {
      if (!ref || ref.type !== "DEVICE" || seen.has(ref.id)) return;
      seen.add(ref.id);
      out.push({ id: ref.id, name, kind });
    };
    const refs = this.site.refs;
    // Order matters for UX: BESS first, then grid, then controller, aux, pv.
    for (const [bessId, ref] of Object.entries(refs.bess)) {
      const bessLabel =
        this.site.topology.bess.find((b) => b.id === bessId)?.name ?? bessId;
      push(ref, bessLabel, "bess");
    }
    push(refs.grid, "Grid meter", "grid");
    push(refs.controller, "Controller", "controller");
    refs.aux.forEach((ref, idx) => push(ref, `Aux ${idx + 1}`, "aux"));
    push(refs.pv, "PV inverter", "pv");
    return out;
  }

  // ------------------------------------------------------------------
  // Authenticated TB user (operator identity, DATA-10)
  // ------------------------------------------------------------------

  /** Lazy one-shot cache of `/api/auth/user` — shared by currentUserName()
   *  and discoverBiddingZones() so the endpoint is hit at most once per
   *  adapter. Resolves null on any failure (never rejects). */
  private authUser: Promise<Awaited<ReturnType<typeof getCurrentUser>> | null> | null = null;

  private fetchAuthUser(): Promise<Awaited<ReturnType<typeof getCurrentUser>> | null> {
    this.authUser ??= getCurrentUser(this.http)
      .catch(() => null)
      .then((me) => {
        // Only memoize SUCCESS — a transient boot 401 must not pin the
        // operator identity (and bidding-zone scoping) to null for the
        // whole session; the next caller retries the endpoint.
        if (me === null) this.authUser = null;
        return me;
      });
    return this.authUser;
  }

  /**
   * Display name of the authenticated TB user: "First Last" when profile
   * names are set, else the account email; null when the endpoint is
   * unreachable. The store uses this to default the operator identity
   * for the alarm audit trail (DATA-10) — it never overrides an
   * explicitly-set name.
   */
  async currentUserName(): Promise<string | null> {
    const me = await this.fetchAuthUser();
    if (!me) return null;
    const name = [me.firstName, me.lastName].filter(Boolean).join(" ").trim();
    return name || me.email || null;
  }

  // ------------------------------------------------------------------
  // Wholesale-electricity bidding zones (Market price page)
  // ------------------------------------------------------------------
  //
  // Discovers zone assets via TB's entity-group API — works for both
  // TENANT_ADMIN and CUSTOMER_USER (a tenant-owned zone shared via a
  // read-only asset GROUP is visible to the customer). Optional
  // per-customer scoping via `UsesPriceZone` relations narrows what a
  // customer user sees. Ported from the TB-EMS hook
  // `useBiddingZoneAssets`.

  /** Default group name to match first (fast path — fetch one group's members). */
  private static readonly PRICE_GROUP_NAME = "Energy Prices General";
  /** Default relation type for per-customer scoping (`<customer>--rel-->zone`). */
  private static readonly PRICE_ZONE_RELATION = "UsesPriceZone";

  async discoverBiddingZones(): Promise<{ zones: ZoneAsset[]; scoped: boolean }> {
    // 1) Asset groups the current user can read.
    const groups = await listEntityGroups(this.http, "ASSET").catch(() => []);
    if (groups.length === 0) return { zones: [], scoped: false };

    // 2) Pick candidate groups: prefer the canonical name; otherwise scan
    //    every non-"All" group; only fall back to the "All" group when
    //    there is literally nothing else (avoids paging through a big
    //    tenant's whole catalogue just to find a handful of zones).
    const named = groups.filter((g) => g.name === TbAdapter.PRICE_GROUP_NAME);
    const candidateGroups = named.length > 0
      ? named
      : (groups.filter((g) => !g.groupAll).length > 0
          ? groups.filter((g) => !g.groupAll)
          : groups);

    // 3) Fan out a member fetch per candidate group, in parallel.
    const memberPages = await Promise.all(
      candidateGroups.map((g) =>
        listEntityGroupAssets(this.http, g.id.id, { pageSize: 100 }).catch(() => null),
      ),
    );

    // 4) Flatten → dedupe → keep bidding zones (NOT the `…_root` parent).
    const seen = new Set<string>();
    const allZones: ZoneAsset[] = [];
    for (const page of memberPages) {
      if (!page) continue;
      for (const a of page.data) {
        if (a.type !== BIDDING_ZONE_ASSET_TYPE) continue;
        if (seen.has(a.id.id)) continue;
        seen.add(a.id.id);
        allZones.push({
          id: a.id.id,
          name: a.name,
          label: a.label?.trim() ? a.label : null,
        });
      }
    }
    if (allZones.length === 0) return { zones: [], scoped: false };

    // 5) Per-customer scoping — customer users only, and only when the
    //    tenant has configured scoping relations.
    const me = await this.fetchAuthUser();
    const isCustomer = me?.authority === "CUSTOMER_USER";
    if (!isCustomer) return { zones: allZones, scoped: false };

    const mine = new Set<string>();
    if (me?.customerId?.id) mine.add(me.customerId.id);

    // Query relations INTO each zone, looking for `<my entity> -[UsesPriceZone]-> zone`.
    const relsPerZone = await Promise.all(
      allZones.map((z) =>
        listRelationsTo(this.http, { type: "ASSET", id: z.id }).catch(() => []),
      ),
    );

    let anyScopeRelation = false;
    const matched = new Set<string>();
    allZones.forEach((z, i) => {
      for (const r of relsPerZone[i] ?? []) {
        if (r.type !== TbAdapter.PRICE_ZONE_RELATION) continue;
        anyScopeRelation = true;
        if (mine.has(r.from.id)) matched.add(z.id);
      }
    });

    // No scoping relations anywhere → tenant hasn't configured scoping →
    // show every zone the customer can see.
    if (!anyScopeRelation) return { zones: allZones, scoped: false };
    return { zones: allZones.filter((z) => matched.has(z.id)), scoped: true };
  }

  async getZoneAttributes(
    zoneId: string,
    keys: readonly string[],
  ): Promise<ZoneAttributeEntry[]> {
    return getAttributes(this.http, { type: "ASSET", id: zoneId }, "SERVER_SCOPE", keys).catch(
      () => [] as ZoneAttributeEntry[],
    );
  }

  async getZoneTimeseries(
    zoneId: string,
    keys: readonly string[],
    opts: { startTs: number; endTs: number; limit?: number },
  ): Promise<Record<string, ZoneTsPoint[]>> {
    const raw = await getTimeseries(this.http, { type: "ASSET", id: zoneId }, {
      keys: [...keys],
      startTs: opts.startTs,
      endTs: opts.endTs,
      limit: opts.limit ?? 50000,
      agg: "NONE",
    }).catch(() => ({} as Record<string, Array<{ ts: number; value: unknown }>>));
    // Coerce values to numbers — TB returns strings for some series.
    const out: Record<string, ZoneTsPoint[]> = {};
    for (const [k, pts] of Object.entries(raw)) {
      out[k] = pts
        .map((p) => ({ ts: p.ts, value: Number(p.value) }))
        .filter((p) => Number.isFinite(p.value));
    }
    return out;
  }

  // ------------------------------------------------------------------
  // Per-site SERVER_SCOPE attribute storage
  // ------------------------------------------------------------------

  async getSiteAttribute(key: string): Promise<string | number | boolean | null> {
    const rows = await getAttributes(this.http, this.site.site, "SERVER_SCOPE", [key]).catch(
      () => [] as Array<{ key: string; value: unknown }>,
    );
    const hit = rows.find((r) => r.key === key);
    if (!hit) return null;
    const v = hit.value;
    if (v === null || v === undefined) return null;
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
    return null;
  }

  async setSiteAttribute(
    key: string,
    value: string | number | boolean | null,
  ): Promise<void> {
    // TB has no "delete attribute" REST endpoint we can rely on from a
    // customer user — writing `null` simply stores null, which we treat
    // as "unset" on read. Sufficient for the operator UX (clear field →
    // chart goes back to its empty state).
    await saveServerAttribute(this.http, this.site.site, { [key]: value });
  }

  destroy(): void {
    // A destroyed adapter's pending grace timer must never fire a late
    // auth-expired notification at its successor's store wiring.
    this.cancelAuthExpiryGrace();
    this.assembler.stop();
    this.ws.close();
  }
}

/**
 * The SPA's alarm-rule taxonomy (Alarm["rule_type"] union in types.ts) —
 * used to validate the `details.rule_type` the edge engine writes into TB
 * alarms before trusting it. Anything else maps to "" (chip omitted).
 */
const VALID_RULE_TYPES: ReadonlySet<string> = new Set([
  "threshold", "range", "boolean", "inactivity", "stuck", "rate", "delta",
  "daily_energy", "excessive_cycling", "peak_demand", "idle_consumption",
  "expression",
]);

/**
 * Sort keys so the "Browse all telemetry" panel reads naturally:
 *   1. Live operational metrics first (kW, kVAr, soc%, voltage, temp, …).
 *   2. Discrete state signals (mode, on/off, fault count).
 *   3. Cumulative counters (EP, EN).
 *   4. AE2-derived buckets, grouped by counter then period.
 *
 * Within each bucket, alphabetical. Keeps the most useful keys at the
 * top of every device card and the long AE2 tail neatly grouped below.
 */
function sortKeysForOperator(keys: string[]): string[] {
  const rank = (k: string): number => {
    if (/^EP$|^EN$/.test(k)) return 30;
    if (/^P_(EP|EN)_5$/.test(k)) return 40;
    if (/^(EP|EN|Etot)_(15|h|d|w|m|y)(_abs|_consumption)?$/.test(k)) return 50;
    if (/_kW$|_kVAr$/.test(k)) return 10;
    if (/(_%$|soc|soh)/i.test(k)) return 12;
    if (/_V($|_)/.test(k)) return 14;
    if (/_A$/.test(k)) return 15;
    if (/_Hz$/.test(k)) return 16;
    if (/_C$/.test(k)) return 17;
    if (/(mode|onoff|state|source|setpoint|actively|auto_)/i.test(k)) return 20;
    return 99;
  };
  return [...keys].sort((a, b) => {
    const ra = rank(a); const rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
}

/* -------------------------------------------------------------------------- */
/* Schedule wire-format conversion                                            */
/*                                                                            */
/* Controller-service's `bess_schedule` SHARED_SCOPE attribute uses the same  */
/* JSON schema as EMS-Admin's local schedule.json — HH:MM strings, optional   */
/* date_start/date_end, an empty `params` object when the mode takes none.    */
/* This SPA models entries with `start_min`/`end_min` integers because that's */
/* cheaper to render in the grid; we convert at the boundary.                 */
/* -------------------------------------------------------------------------- */

interface WireScheduleEntry {
  id: string;
  name?: string | null;
  enabled?: boolean;
  priority?: number;
  days_of_week?: number[];
  time_start?: string;
  time_end?: string;
  date_start?: string | null;
  date_end?: string | null;
  mode: ControllerMode;
  params?: Record<string, number | string | boolean>;
}

interface ScheduleEnvelope {
  version?: number;
  updatedAt?: number;
  updatedBy?: string;
  name?: string;
  description?: string;
  timezone?: string;
  default?: { mode: ControllerMode; params: Record<string, unknown> };
  entries?: WireScheduleEntry[];
}

function parseHm(s: string | undefined): number {
  if (!s) return 0;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return 0;
  const h = Math.max(0, Math.min(24, Number(m[1])));
  const mm = Math.max(0, Math.min(59, Number(m[2])));
  return h * 60 + mm;
}

function fmtHm(min: number): string {
  // 1440 == 24:00 (end-of-day token the controller-service accepts).
  if (min >= 1440) return "24:00";
  const h = Math.floor(Math.max(0, min) / 60);
  const m = Math.max(0, min) % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function wireEntryToInternal(e: WireScheduleEntry): ScheduleEntry | null {
  if (!e || !e.id || !e.mode) return null;
  // "24:00" decodes as 1440 — preserved across save/load so an end-of-day
  // entry round-trips unchanged.
  const startMin = e.time_start === "24:00" ? 1440 : parseHm(e.time_start);
  const endMin = e.time_end === "24:00" ? 1440 : parseHm(e.time_end);
  const params: Record<string, number | string | boolean> = {};
  for (const [k, v] of Object.entries(e.params ?? {})) {
    if (typeof v === "number" || typeof v === "string" || typeof v === "boolean") {
      params[k] = v;
    }
  }
  return {
    id: e.id,
    name: e.name ?? "",
    enabled: e.enabled !== false,
    priority: typeof e.priority === "number" ? e.priority : 0,
    days_of_week: Array.isArray(e.days_of_week) ? e.days_of_week.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6) : [],
    start_min: startMin,
    end_min: endMin,
    mode: e.mode,
    params,
    date_start: e.date_start ?? null,
    date_end: e.date_end ?? null,
  };
}

function internalEntryToWire(e: ScheduleEntry): WireScheduleEntry {
  return {
    id: e.id,
    name: e.name || undefined,
    enabled: e.enabled,
    priority: e.priority,
    days_of_week: e.days_of_week,
    time_start: fmtHm(e.start_min),
    time_end: fmtHm(e.end_min),
    mode: e.mode,
    params: e.params ?? {},
    date_start: e.date_start || null,
    date_end: e.date_end || null,
  };
}

/**
 * Lift a wire envelope into the typed ScheduleDoc the page consumes.
 * Returns null only when the envelope itself is missing; an envelope with
 * zero entries is still returned (operator-typed defaults are meaningful).
 */
function envelopeToDoc(env: ScheduleEnvelope, fallbackTz: string): ScheduleDoc {
  const entries = (env.entries ?? [])
    .map(wireEntryToInternal)
    .filter((e): e is ScheduleEntry => e !== null);
  const defaultMode = env.default?.mode ?? "gridZeroControl";
  const defaultParams: Record<string, number> = {};
  for (const [k, v] of Object.entries(env.default?.params ?? {})) {
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n)) defaultParams[k] = n;
  }
  return {
    version: 1,
    timezone: env.timezone || fallbackTz,
    default: { mode: defaultMode, params: defaultParams },
    entries,
    meta: {
      name: env.name,
      description: env.description,
      updatedBy: env.updatedBy,
      updatedAt: env.updatedAt,
    },
  };
}

/**
 * Pure: project an arbitrary epoch-ms into the schedule's timezone and
 * return the local weekday (0=Mon..6=Sun) plus minute-of-day. Used for
 * "what entry is active right now" without DOM access.
 */
function projectInTz(tz: string, atMs: number): { weekday: number; minute: number; ymd: string } {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hourCycle: "h23",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const parts = fmt.formatToParts(new Date(atMs));
    const lookup = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    const map: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
    const weekday = map[lookup("weekday")] ?? 0;
    const minute = parseInt(lookup("hour"), 10) * 60 + parseInt(lookup("minute"), 10);
    const ymd = `${lookup("year")}-${lookup("month")}-${lookup("day")}`;
    return { weekday, minute, ymd };
  } catch {
    const d = new Date(atMs);
    return {
      weekday: (d.getDay() + 6) % 7,
      minute: d.getHours() * 60 + d.getMinutes(),
      ymd: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    };
  }
}

function entryCovers(e: ScheduleEntry, weekday: number, minute: number, ymd: string): boolean {
  if (!e.enabled) return false;
  if (!e.days_of_week.includes(weekday)) return false;
  if (e.date_start && ymd < e.date_start) return false;
  if (e.date_end && ymd > e.date_end) return false;
  const s = e.start_min;
  const en = e.end_min;
  if (s === en) return false;
  if (en < s) {
    // wraparound (e.g. 22:00 → 06:00)
    return minute >= s || minute < en;
  }
  return s <= minute && minute < en;
}

function resolveActiveEntry(entries: ScheduleEntry[], tz: string, atMs: number): ScheduleEntry | undefined {
  const { weekday, minute, ymd } = projectInTz(tz, atMs);
  const covering = entries.filter((e) => entryCovers(e, weekday, minute, ymd));
  if (covering.length === 0) return undefined;
  covering.sort((a, b) => (b.priority - a.priority) || a.id.localeCompare(b.id));
  return covering[0];
}

/**
 * Walk forward at 1-minute resolution for up to 8 days; return the first
 * instant where the resolved entry changes (id or default fallback). Used
 * to drive the "next change at HH:MM" banner.
 */
function nextChangeAfter(
  entries: ScheduleEntry[],
  defaultMode: ControllerMode,
  tz: string,
  fromMs: number,
): { at: number; mode: ControllerMode } | undefined {
  const startMin = fromMs - (fromMs % 60_000);
  const initial = resolveActiveEntry(entries, tz, startMin);
  const initialId = initial?.id ?? null;
  for (let i = 1; i <= 8 * 24 * 60; i++) {
    const t = startMin + i * 60_000;
    const e = resolveActiveEntry(entries, tz, t);
    const id = e?.id ?? null;
    if (id !== initialId) {
      return { at: t, mode: e?.mode ?? defaultMode };
    }
  }
  return undefined;
}

/** Parse the `bess_schedule_status` / `bess_manual_override_status` echo. */
function parseScheduleStatus(raw: unknown): ScheduleStatus | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const kind = String(o.echo_kind ?? "heartbeat");
  const validKinds: ScheduleStatus["echo_kind"][] = ["applied", "rejected", "stale", "deleted", "heartbeat"];
  const echo_kind: ScheduleStatus["echo_kind"] = (validKinds as string[]).includes(kind)
    ? (kind as ScheduleStatus["echo_kind"])
    : "heartbeat";
  const rejectedRaw = Array.isArray(o.rejected_entries) ? (o.rejected_entries as unknown[]) : [];
  const rejected_entries = rejectedRaw
    .map((r): ScheduleStatus["rejected_entries"][number] | null => {
      if (!r || typeof r !== "object") return null;
      const rr = r as Record<string, unknown>;
      return {
        id: typeof rr.id === "string" ? rr.id : undefined,
        index: typeof rr.index === "number" ? rr.index : undefined,
        error: String(rr.error ?? "unknown"),
      };
    })
    .filter((x): x is ScheduleStatus["rejected_entries"][number] => x !== null);
  return {
    echo_kind,
    applied_updatedAt: typeof o.applied_updatedAt === "number" ? o.applied_updatedAt : null,
    applied_at_ms: typeof o.applied_at_ms === "number" ? o.applied_at_ms : null,
    applied_entry_count: typeof o.applied_entry_count === "number" ? o.applied_entry_count : null,
    rejected_entries,
    error: typeof o.error === "string" ? o.error : undefined,
    active_source: typeof o.active_source === "string" ? o.active_source : undefined,
    current_mode: typeof o.current_mode === "string" ? (o.current_mode as ControllerMode) : undefined,
    next_change_at_ms: typeof o.next_change_at_ms === "number" ? o.next_change_at_ms : null,
    edge: (o.edge as ScheduleStatus["edge"]) ?? undefined,
    stats: (o.stats as Record<string, number>) ?? undefined,
  };
}

/**
 * Aggregate-mode timeseries fetch for a single (entity, key). Used by the
 * 60-minute backfill in `recentHistory()` — TB's AVG aggregation aligns
 * every device's samples to the same bucket grid, so cross-device joins
 * downstream are trivial.
 */
async function fetchAgg(
  http: TbHttp,
  entity: EntityRef,
  key: string,
  startTs: number,
  endTs: number,
  intervalMs: number,
): Promise<Array<{ ts: number; value: number }>> {
  try {
    const raw = await getTimeseries(http, entity, {
      keys: [key],
      startTs,
      endTs,
      limit: 5_000,
      agg: "AVG",
      interval: intervalMs,
    });
    return (raw[key] ?? []).map((p) => ({
      ts: p.ts,
      value: typeof p.value === "number" ? p.value : Number(p.value),
    })).filter((p) => Number.isFinite(p.value));
  } catch {
    return [];
  }
}

/**
 * Per-BESS abstract-key → EMS-server-telemetry-key mapping. Used by the
 * BESS detail telemetry browser. Each entry says which key the EMS server
 * actually stores under, and the scale + sign flip needed for the SPA's
 * canonical sign convention (positive = into the battery / into the bus).
 *
 * Set `controller: true` when the underlying value lives on the controller
 * device rather than the BESS device (e.g. commanded setpoints).
 */
interface BessKeyMap { tbKey: string; scale: number; controller?: boolean }
const BESS_KEY_MAP: Record<string, BessKeyMap> = {
  ac_active_power:   { tbKey: "battery_kW",           scale: -1000 },
  ac_reactive_power: { tbKey: "battery_kVAr",         scale:  1000 },
  soc:               { tbKey: "battery_soc_%",        scale:  1    },
  total_voltage:     { tbKey: "total_voltage_V",      scale:  1    },
  // The clean `ambient_temperature_C` isn't published on the live Zhiguang
  // fleet; the cloud-connector emits the raw `ambient_temperature` (already °C).
  // Map to that so the historical/inspect "Ambient temperature" series isn't
  // empty (mirrors the live-snapshot preference in snapshotFromTb.ts).
  ambient_temperature: { tbKey: "ambient_temperature", scale: 1  },
  fault_count:       { tbKey: "fault_count",          scale:  1    },
  bess_onoff:        { tbKey: "bess_onoff",           scale:  1    },
  setpoint_active:   { tbKey: "ctrl_pwr_kW",          scale:  1000, controller: true },
  setpoint_reactive: { tbKey: "ctrl_pwr_kVAr",        scale:  1000, controller: true },
};

/**
 * Map our internal granularity tag to the EMS-analytics key suffix.
 * Source of truth: `derivedKeys.ts` `DeltaPeriod` union, which mirrors
 * the AE2 scheduler's bucket sizes (`EP_15`, `EP_h`, `EP_d`, …).
 */
function tbBucketSuffix(g: EnergyGran): string {
  switch (g) {
    case "15m": return "15";
    case "h":   return "h";
    case "d":   return "d";
    case "w":   return "w";
    case "m":   return "m";
    case "y":   return "y";
  }
}

/**
 * Inner join of two timeseries (EP and EN) into per-bucket samples.
 * The AE2 scheduler emits both keys on the same bucket grid, so we
 * pivot by exact ts equality. Any bucket where only one side is
 * present still yields a sample (the missing side counts as 0 kWh —
 * a zero charge or zero discharge that bucket).
 */
function alignChargeDischarge(
  epPoints: Array<{ ts: number; value: number | string | boolean }>,
  enPoints: Array<{ ts: number; value: number | string | boolean }>,
): BessEnergySample[] {
  const map = new Map<number, BessEnergySample>();
  for (const p of epPoints) {
    const v = typeof p.value === "number" ? p.value : Number(p.value);
    if (!Number.isFinite(v)) continue;
    map.set(p.ts, { ts: p.ts, charged_kWh: v, discharged_kWh: 0 });
  }
  for (const p of enPoints) {
    const v = typeof p.value === "number" ? p.value : Number(p.value);
    if (!Number.isFinite(v)) continue;
    const cur = map.get(p.ts);
    if (cur) cur.discharged_kWh = v;
    else map.set(p.ts, { ts: p.ts, charged_kWh: 0, discharged_kWh: v });
  }
  return [...map.values()].sort((a, b) => a.ts - b.ts);
}

function granToMs(gran: HistoricalQuery["granularity"]): number | undefined {
  switch (gran) {
    case "raw": return undefined;
    case "2m":  return 2 * 60_000;
    case "5m":  return 5 * 60_000;
    case "15m": return 15 * 60_000;
    case "h":   return 3_600_000;
    case "d":   return 24 * 3_600_000;
    case "w":   return 7 * 24 * 3_600_000;
    case "m":   return 30 * 24 * 3_600_000;
    case "y":   return 365 * 24 * 3_600_000;
  }
}

/**
 * Cross-device aggregation with **per-device forward-fill**.
 *
 * Why this matters for raw mode: each BESS device samples on its own
 * clock, so a given 60-second bucket might only contain a sample from
 * (say) one of four devices. A naïve SUM-of-contributing-samples would
 * report 1/4 of the true fleet power → the chart appears to "drop to
 * zero" between sample times. Forward-filling each device's last known
 * value across empty buckets gives a faithful aggregate even when
 * different devices send samples at staggered times.
 *
 * Two operating modes:
 *
 *   - `bucketMs > 0`: timestamps are rounded DOWN to `bucketMs`. Required
 *     for raw mode (devices on independent clocks).
 *
 *   - `bucketMs <= 0`: timestamps match exactly. Use when TB already
 *     returned AVG-aggregated samples on a fixed interval grid.
 *
 * The first bucket emitted is the FIRST bucket where ALL devices have
 * been seen at least once (for SUM) or any device has been seen (for
 * AVG) — this avoids reporting partial fleets at the leading edge.
 *
 * Within a single device, multiple samples in the same bucket are first
 * averaged so two close samples don't double-weight.
 */
type AggOp = "sum" | "avg";
function alignedAggregate(
  seriesList: Array<Array<{ ts: number; value: number }>>,
  op: AggOp,
  bucketMs: number,
): Array<{ ts: number; value: number }> {
  if (!seriesList.length) return [];

  // Step 1: per-device, group samples into buckets and average within bucket.
  const perDevice: Array<Map<number, number>> = seriesList.map((series) => {
    const local = new Map<number, { sum: number; n: number }>();
    for (const p of series) {
      if (!Number.isFinite(p.value)) continue;
      const b = bucketMs > 0 ? Math.floor(p.ts / bucketMs) * bucketMs : p.ts;
      const cur = local.get(b) ?? { sum: 0, n: 0 };
      cur.sum += p.value;
      cur.n += 1;
      local.set(b, cur);
    }
    const out = new Map<number, number>();
    for (const [b, { sum, n }] of local) out.set(b, sum / n);
    return out;
  });

  // Step 2: collect all bucket timestamps across devices, sorted.
  const allBuckets = new Set<number>();
  for (const d of perDevice) for (const ts of d.keys()) allBuckets.add(ts);
  const sortedBuckets = [...allBuckets].sort((a, b) => a - b);

  // Step 3: walk forward, carrying each device's last-known value.
  const lastKnown: number[] = new Array(perDevice.length).fill(NaN);
  const seen: boolean[] = new Array(perDevice.length).fill(false);
  const out: Array<{ ts: number; value: number }> = [];

  for (const ts of sortedBuckets) {
    // Update each device's last-known value if it has a fresh sample.
    for (let i = 0; i < perDevice.length; i++) {
      const v = perDevice[i].get(ts);
      if (v !== undefined && Number.isFinite(v)) {
        lastKnown[i] = v;
        seen[i] = true;
      }
    }

    if (op === "sum") {
      // For SUM we need EVERY device to have been seen at least once
      // (otherwise we'd undercount the fleet). Once warmed up, all
      // devices contribute their last-known value at every bucket.
      if (seen.some((s) => !s)) continue;
      let sum = 0;
      for (const v of lastKnown) sum += v;
      out.push({ ts, value: sum });
    } else {
      // For AVG, any device with data contributes. Average over those.
      let sum = 0; let n = 0;
      for (let i = 0; i < lastKnown.length; i++) {
        if (seen[i]) { sum += lastKnown[i]; n++; }
      }
      if (n > 0) out.push({ ts, value: sum / n });
    }
  }
  return out;
}

/** Sum across devices. Uses 60-s buckets for raw, exact-ts for aggregated. */
function alignedSum(seriesList: Array<Array<{ ts: number; value: number }>>, bucketMs = 60_000): Array<{ ts: number; value: number }> {
  return alignedAggregate(seriesList, "sum", bucketMs);
}

/** Average across devices. Uses 60-s buckets for raw, exact-ts for aggregated. */
function alignedAverage(seriesList: Array<Array<{ ts: number; value: number }>>, bucketMs = 60_000): Array<{ ts: number; value: number }> {
  return alignedAggregate(seriesList, "avg", bucketMs);
}

